import { useState, useCallback, useMemo, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import type { Template, ContractReviewForm } from "../../types/template";
import type {
  CreateLegalEntityInput,
  LegalEntity,
} from "../../types/legalEntity";
import type {
  CreateRepresentativeInput,
  Representative,
} from "../../types/representative";
import {
  applyPlaceholders,
  finalizeContractReviewPlaceholders,
  TRACK_CHANGES_APPLY_FAILED_PREFIX,
} from "../../services/wordService";
import {
  getEmailFieldError,
  getPhoneFieldError,
  validateAddClientInput,
  validateAddRepresentativeInput,
} from "../../utils/validation/legalEntityValidation";
import { PhoneNumberInput } from "../molecules/PhoneNumberInput";
import { InputWithDropdown } from "../molecules/InputWithDropdown";
import { resolveLocationFieldResets } from "../../utils/validation/locationConsistency";
import { useLocationOptions } from "../../hooks/useLocationOptions";
import {
  hasActiveSharePointItem,
  saveActiveFileToSharePoint,
} from "../../services/graphService";
import { DEFAULT_BUSINESS_UNIT_NAME } from "../../constants/defaultBusinessUnit";
import type { BusinessUnitOption } from "../../types/businessUnit";
import { ALL_BUSINESS_UNITS_SENTINEL } from "../../utils/businessUnitApiCodes";
import { formatLegalEntityCityStateZip } from "../../services/legalEntityService";
import { formatRepresentativeCityStateZip } from "../../services/representativeService";
import {
  buildContractReviewFormPayload,
  isCoreContractReviewFormValid,
} from "../../utils/contractReviewFormCore";

const CONTRACT_ORIGINATIONS = ["External", "Internal"] as const;

/** Template metadata may include this sentinel; it is not a real BU choice for generated contracts. */
const ALL_BUSINESSES_SENTINEL = "All BUs";

const buildAppliedFormSignature = (form: ContractReviewForm): string =>
  JSON.stringify({
    contractName: form.contractName,
    templateId: form.templateId,
    contractType: form.contractType,
    businessUnit: form.businessUnit,
    contractOrigination: form.contractOrigination,
    effectiveDate: form.effectiveDate,
    legalEntityId: form.legalEntityId,
    legalEntityName: form.legalEntityName,
    contactName: form.contactName,
    contactTitle: form.contactTitle,
    representativeId: form.representativeId,
    representativeName: form.representativeName,
    representativeTitle: form.representativeTitle,
    representativeCompanyName: form.representativeCompanyName,
    representativeAddress: form.representativeAddress,
    representativeStreetAddress: form.representativeStreetAddress,
    representativeCityStateZip: form.representativeCityStateZip,
    representativePhone: form.representativePhone,
    representativeEmail: form.representativeEmail,
    planGroupName: form.planGroupName,
    addressMerged: form.addressMerged,
    companyStreetAddress: form.companyStreetAddress,
    companyCityStateZip: form.companyCityStateZip,
    phone: form.phone,
    email: form.email,
  });

const formatLocalDateYmd = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

type ContractReviewDetailsViewProps = {
  template: Template;
  legalEntities: LegalEntity[];
  representatives: Representative[];
  /** Active BUs from GET /business-units (includes synthetic All BUs row). */
  businessUnitRows: readonly BusinessUnitOption[];
  legalEntitiesError?: string | null;
  representativesError?: string | null;
  /** Shown after a successful host save, before commit completes (e.g. SharePoint). */
  hostPersistSuccess?: string | null;
  /** Persist/commit failures for Generate Contract. */
  hostPersistError?: string | null;
  /**
   * When set (template flow with document name modal), **Contract Name** is read-only and matches the draft’s display name.
   */
  lockedContractName?: string | null;
  onCreateLegalEntity: (input: CreateLegalEntityInput) => Promise<LegalEntity>;
  onLegalEntitiesRefresh: () => Promise<LegalEntity[]>;
  onCreateRepresentative: (
    input: CreateRepresentativeInput,
  ) => Promise<Representative>;
  onRepresentativesRefresh: () => Promise<Representative[]>;
  onGenerateContract: (form: ContractReviewForm) => void | Promise<void>;
};

export const ContractReviewDetailsView = ({
  template,
  legalEntities,
  representatives,
  businessUnitRows,
  legalEntitiesError = null,
  representativesError = null,
  hostPersistSuccess = null,
  hostPersistError = null,
  lockedContractName = null,
  onCreateLegalEntity,
  onLegalEntitiesRefresh,
  onCreateRepresentative,
  onRepresentativesRefresh,
  onGenerateContract,
}: ContractReviewDetailsViewProps) => {
  const isContractNameLocked = (() => {
    const t = (lockedContractName ?? "").trim();
    return t.length > 0;
  })();

  const [contractName, setContractName] = useState(
    () => lockedContractName?.trim() ?? "",
  );
  useEffect(() => {
    const t = (lockedContractName ?? "").trim();
    if (t) {
      setContractName(t);
    }
  }, [lockedContractName]);
  const [contractOrigination, setContractOrigination] = useState("Internal");
  const [effectiveDate, setEffectiveDate] = useState(formatLocalDateYmd);
  const [legalEntityId, setLegalEntityId] = useState<string>("");
  const [representativeId, setRepresentativeId] = useState<string>("");
  const [hasAppliedChanges, setHasAppliedChanges] = useState(false);
  const [appliedFormSignature, setAppliedFormSignature] = useState<
    string | null
  >(null);
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [requiresManualRevert, setRequiresManualRevert] = useState(false);
  const [isGeneratingContract, setIsGeneratingContract] = useState(false);
  const [graphPersistSuccess, setGraphPersistSuccess] = useState<string | null>(
    null,
  );
  const [graphPersistError, setGraphPersistError] = useState<string | null>(
    null,
  );
  const [planGroupName, setPlanGroupName] = useState("");
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<string[]>(
    [],
  );
  const [isBusinessUnitOpen, setIsBusinessUnitOpen] = useState(false);
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [addClientError, setAddClientError] = useState<string | null>(null);
  /** After first Save in Add Client, show field-level validation (derived from newClientForm). */
  const [addClientSubmitAttempted, setAddClientSubmitAttempted] =
    useState(false);
  const [addClientTouched, setAddClientTouched] = useState<
    Partial<Record<keyof CreateLegalEntityInput, boolean>>
  >({});
  const [isAddRepresentativeOpen, setIsAddRepresentativeOpen] = useState(false);
  const [isCreatingRepresentative, setIsCreatingRepresentative] =
    useState(false);
  const [addRepresentativeError, setAddRepresentativeError] = useState<
    string | null
  >(null);
  const [
    addRepresentativeSubmitAttempted,
    setAddRepresentativeSubmitAttempted,
  ] = useState(false);
  const [addRepresentativeTouched, setAddRepresentativeTouched] = useState<
    Partial<Record<keyof CreateRepresentativeInput, boolean>>
  >({});
  const [addRepresentativePhoneTouched, setAddRepresentativePhoneTouched] =
    useState(false);
  const [addRepresentativeEmailTouched, setAddRepresentativeEmailTouched] =
    useState(false);
  const [newClientForm, setNewClientForm] = useState<CreateLegalEntityInput>({
    name: "",
    title: "",
    company: "",
    streetAddress: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: "",
  });
  const [newRepresentativeForm, setNewRepresentativeForm] =
    useState<CreateRepresentativeInput>({
      name: "",
      title: "",
      company: "",
      streetAddress: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
      email: "",
    });
  const {
    states: locationStates,
    cities: locationCities,
    zips: locationZips,
  } = useLocationOptions(newClientForm.state, newClientForm.city);
  const {
    states: representativeLocationStates,
    cities: representativeLocationCities,
    zips: representativeLocationZips,
  } = useLocationOptions(
    newRepresentativeForm.state,
    newRepresentativeForm.city,
  );

  const newClientValidation = useMemo(
    () => validateAddClientInput(newClientForm),
    [newClientForm],
  );
  const hasNewClientValidationErrors =
    Object.keys(newClientValidation).length > 0;
  const newRepresentativeValidation = useMemo(
    () => validateAddRepresentativeInput(newRepresentativeForm),
    [newRepresentativeForm],
  );
  const hasNewRepresentativeValidationErrors =
    Object.keys(newRepresentativeValidation).length > 0;
  const visibleAddClientErrors = useMemo((): typeof newClientValidation => {
    if (addClientSubmitAttempted) {
      return newClientValidation;
    }
    const v: typeof newClientValidation = {};
    (["company", "streetAddress", "city", "state", "zip"] as const).forEach(
      (k) => {
        if (addClientTouched[k] && newClientValidation[k]) {
          v[k] = newClientValidation[k];
        }
      },
    );
    return v;
  }, [newClientValidation, addClientSubmitAttempted, addClientTouched]);

  const visibleAddRepresentativeErrors =
    useMemo((): typeof newRepresentativeValidation => {
      if (addRepresentativeSubmitAttempted) {
        return newRepresentativeValidation;
      }
      const v: typeof newRepresentativeValidation = {};
      (
        [
          "name",
          "title",
          "company",
          "streetAddress",
          "city",
          "state",
          "zip",
        ] as const
      ).forEach((k) => {
        if (addRepresentativeTouched[k] && newRepresentativeValidation[k]) {
          v[k] = newRepresentativeValidation[k];
        }
      });
      if (addRepresentativePhoneTouched) {
        const e = getPhoneFieldError(newRepresentativeForm.phone);
        if (e) {
          v.phone = e;
        }
      }
      if (addRepresentativeEmailTouched) {
        const e = getEmailFieldError(newRepresentativeForm.email);
        if (e) {
          v.email = e;
        }
      }
      return v;
    }, [
      newRepresentativeForm,
      newRepresentativeValidation,
      addRepresentativeSubmitAttempted,
      addRepresentativeTouched,
      addRepresentativePhoneTouched,
      addRepresentativeEmailTouched,
    ]);

  const selectedEntity = legalEntities.find((e) => e.id === legalEntityId);
  const selectedRepresentative = representatives.find(
    (r) => r.id === representativeId,
  );
  const templateBusinessUnits = useMemo(
    () =>
      template.businessUnits
        ?.map((u) => u.trim())
        .filter((u) => u.length > 0) ?? [],
    [template.businessUnits],
  );

  const contactNameDisplay = selectedEntity?.contactName ?? "";
  const contactTitleDisplay = selectedEntity?.contactTitle ?? "";
  const addressDisplay = selectedEntity?.address?.trim() ?? "";
  const companyStreetDisplay = selectedEntity?.street?.trim() ?? "";
  const companyCityStateZipDisplay = selectedEntity
    ? formatLegalEntityCityStateZip(selectedEntity)
    : "";
  const phoneDisplay = selectedEntity?.phone?.trim() ?? "";
  const emailDisplay = selectedEntity?.email?.trim() ?? "";
  const representativeNameDisplay = selectedRepresentative?.name?.trim() ?? "";
  const representativeTitleDisplay =
    selectedRepresentative?.title?.trim() ?? "";
  const representativeCompanyDisplay =
    selectedRepresentative?.company?.trim() ?? "";
  const representativeAddressDisplay =
    selectedRepresentative?.address?.trim() ?? "";
  const representativeStreetDisplay =
    selectedRepresentative?.streetAddress?.trim() ?? "";
  const representativeCityStateZipDisplay = selectedRepresentative
    ? formatRepresentativeCityStateZip(selectedRepresentative)
    : "";
  const representativePhoneDisplay =
    selectedRepresentative?.phone?.trim() ?? "";
  const representativeEmailDisplay =
    selectedRepresentative?.email?.trim() ?? "";
  const selectableBusinessUnits = useMemo(
    () =>
      businessUnitRows.filter(
        (o) => o.code !== ALL_BUSINESS_UNITS_SENTINEL.code,
      ),
    [businessUnitRows],
  );
  const allBusinessUnitsSelected =
    selectableBusinessUnits.length > 0 &&
    selectedBusinessUnits.length === selectableBusinessUnits.length &&
    selectableBusinessUnits.every((o) =>
      selectedBusinessUnits.includes(o.code),
    );
  const selectedBusinessUnitDisplay = useMemo(() => {
    if (selectedBusinessUnits.length === 0) {
      return "Select business units";
    }
    return selectedBusinessUnits
      .map((code) => {
        const row = businessUnitRows.find(
          (o) => o.code.toUpperCase() === code.trim().toUpperCase(),
        );
        return row?.name ?? code;
      })
      .join(", ");
  }, [selectedBusinessUnits, businessUnitRows]);

  useEffect(() => {
    const real = selectableBusinessUnits;
    if (real.length === 0) {
      setSelectedBusinessUnits([]);
      return;
    }
    const wantsAllBusinessUnits = templateBusinessUnits.some((t) => {
      const tr = t.trim();
      if (!tr) {
        return false;
      }
      return (
        tr === ALL_BUSINESSES_SENTINEL ||
        tr.toUpperCase() === ALL_BUSINESS_UNITS_SENTINEL.code.toUpperCase() ||
        tr.toLowerCase() === ALL_BUSINESS_UNITS_SENTINEL.name.toLowerCase()
      );
    });
    if (wantsAllBusinessUnits) {
      setSelectedBusinessUnits(real.map((o) => o.code));
      return;
    }
    const resolveToCode = (t: string): string | undefined => {
      const tr = t.trim();
      if (!tr) {
        return undefined;
      }
      const byCode = real.find(
        (o) => o.code.toUpperCase() === tr.toUpperCase(),
      );
      if (byCode) {
        return byCode.code;
      }
      const byName = real.find(
        (o) => o.name === tr || o.name.toLowerCase() === tr.toLowerCase(),
      );
      return byName?.code;
    };
    const fromTemplate = templateBusinessUnits
      .map(resolveToCode)
      .filter((c): c is string => !!c)
      .filter(
        (c) =>
          c.toUpperCase() !== ALL_BUSINESS_UNITS_SENTINEL.code.toUpperCase(),
      );
    if (fromTemplate.length > 0) {
      setSelectedBusinessUnits(fromTemplate);
      return;
    }
    const defaultRow = real.find((o) => o.name === DEFAULT_BUSINESS_UNIT_NAME);
    if (defaultRow) {
      setSelectedBusinessUnits([defaultRow.code]);
      return;
    }
    setSelectedBusinessUnits([real[0].code]);
  }, [template.id, templateBusinessUnits, selectableBusinessUnits]);

  const isCoreFormValid = useMemo(
    () =>
      isCoreContractReviewFormValid({
        contractName,
        businessUnit: selectedBusinessUnits.join(", "),
        contractOrigination,
        effectiveDate,
      }),
    [contractName, selectedBusinessUnits, contractOrigination, effectiveDate],
  );

  const buildForm = useCallback((): ContractReviewForm | null => {
    return buildContractReviewFormPayload({
      contractName,
      templateId: template.id,
      contractType: template.templateType,
      businessUnit: selectedBusinessUnits.join(", "),
      contractOrigination: contractOrigination || "",
      effectiveDate,
      legalEntityId,
      legalEntityName: selectedEntity?.name ?? "",
      contactName: contactNameDisplay,
      contactTitle: contactTitleDisplay,
      representativeId,
      representativeName: representativeNameDisplay,
      representativeTitle: representativeTitleDisplay,
      representativeCompanyName: representativeCompanyDisplay,
      representativeAddress: representativeAddressDisplay,
      representativeStreetAddress: representativeStreetDisplay,
      representativeCityStateZip: representativeCityStateZipDisplay,
      representativePhone: representativePhoneDisplay,
      representativeEmail: representativeEmailDisplay,
      planGroupName,
      addressMerged: addressDisplay,
      companyStreetAddress: companyStreetDisplay,
      companyCityStateZip: companyCityStateZipDisplay,
      phone: phoneDisplay,
      email: emailDisplay,
    });
  }, [
    contractName,
    template.id,
    template.templateType,
    selectedBusinessUnits,
    contractOrigination,
    effectiveDate,
    legalEntityId,
    selectedEntity,
    contactNameDisplay,
    contactTitleDisplay,
    representativeId,
    representativeNameDisplay,
    representativeTitleDisplay,
    representativeCompanyDisplay,
    representativeAddressDisplay,
    representativeStreetDisplay,
    representativeCityStateZipDisplay,
    representativePhoneDisplay,
    representativeEmailDisplay,
    planGroupName,
    addressDisplay,
    companyStreetDisplay,
    companyCityStateZipDisplay,
    phoneDisplay,
    emailDisplay,
  ]);

  const currentFormSignature = useMemo(() => {
    const form = buildForm();
    return form ? buildAppliedFormSignature(form) : null;
  }, [buildForm]);
  const hasAppliedLatestChanges =
    appliedFormSignature !== null &&
    currentFormSignature === appliedFormSignature;
  const hasChangesAfterApply =
    hasAppliedChanges &&
    appliedFormSignature !== null &&
    currentFormSignature !== appliedFormSignature;

  const handleApplyChanges = useCallback(async () => {
    const form = buildForm();
    if (!form) return;
    const formSignature = buildAppliedFormSignature(form);
    try {
      setIsApplyingChanges(true);
      setApplyError(null);
      setRequiresManualRevert(false);
      const result = await applyPlaceholders(form);
      if (result === undefined) {
        setApplyError("Word API is not available in this environment.");
        return;
      }
      const totalReplaced =
        result.taggedUpdated.length +
        result.highlightReplaced.length +
        result.mustacheReplaced.length;
      if (totalReplaced === 0) {
        if (hasAppliedChanges) {
          setRequiresManualRevert(true);
          setApplyError(
            "No editable Contract Review fields were found. If this document was already applied before re-apply support was added, reject or revert the previous tracked placeholder changes in Word, then click I reverted changes and apply again.",
          );
        } else {
          setApplyError(
            "No placeholders were updated. Use yellow-highlighted labels and/or plain [BRACKET] tokens that match addin/src/constants/highlightLabelMap.ts (e.g. [CLIENT NAME], [CONTRACT NAME], [BUSINESS UNIT]). Check F12 logs and form values.",
          );
        }
        return;
      }
      setHasAppliedChanges(true);
      setAppliedFormSignature(formSignature);
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : "Unable to apply changes to the document.";
      if (msg.startsWith(TRACK_CHANGES_APPLY_FAILED_PREFIX)) {
        setApplyError(
          "Word could not apply tracked revisions (Review / Track Changes). Save a copy, check the document is not protected or restricted, then try again.\nDetails: " +
            msg.slice(TRACK_CHANGES_APPLY_FAILED_PREFIX.length + 2),
        );
      } else {
        setApplyError(msg);
      }
    } finally {
      setIsApplyingChanges(false);
    }
  }, [buildForm, hasAppliedChanges]);

  const handleGenerateContract = useCallback(async () => {
    const form = buildForm();
    if (!form) return;
    setIsGeneratingContract(true);
    setGraphPersistSuccess(null);
    setGraphPersistError(null);
    try {
      await finalizeContractReviewPlaceholders();
      if (hasActiveSharePointItem()) {
        try {
          await saveActiveFileToSharePoint();
          setGraphPersistSuccess("Document saved to SharePoint successfully");
        } catch (persistErr: unknown) {
          const msg =
            persistErr instanceof Error
              ? persistErr.message
              : "Unable to save the document to SharePoint.";
          setGraphPersistError(msg);
          return;
        }
      }
      await Promise.resolve(onGenerateContract(form));
    } catch (genErr: unknown) {
      const msg =
        genErr instanceof Error
          ? genErr.message
          : "Unable to generate contract.";
      setGraphPersistError(msg);
    } finally {
      setIsGeneratingContract(false);
    }
  }, [buildForm, onGenerateContract]);

  const handleManualRevertConfirmed = useCallback(() => {
    setHasAppliedChanges(false);
    setAppliedFormSignature(null);
    setRequiresManualRevert(false);
    setApplyError(null);
  }, []);

  const canGenerate =
    isCoreFormValid && hasAppliedLatestChanges && !isGeneratingContract;

  const resetNewClientForm = useCallback(() => {
    setNewClientForm({
      name: "",
      title: "",
      company: "",
      streetAddress: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
      email: "",
    });
    setAddClientSubmitAttempted(false);
    setAddClientError(null);
    setAddClientTouched({});
  }, []);

  const resetNewRepresentativeForm = useCallback(() => {
    setNewRepresentativeForm({
      name: "",
      title: "",
      company: "",
      streetAddress: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
      email: "",
    });
    setAddRepresentativeSubmitAttempted(false);
    setAddRepresentativeError(null);
    setAddRepresentativeTouched({});
    setAddRepresentativePhoneTouched(false);
    setAddRepresentativeEmailTouched(false);
  }, []);

  const handleCreateClient = useCallback(async () => {
    if (isCreatingClient) return;
    setAddClientSubmitAttempted(true);
    const validationErrors = validateAddClientInput(newClientForm);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    try {
      setIsCreatingClient(true);
      setAddClientError(null);
      const created = await onCreateLegalEntity({
        name: newClientForm.name.trim(),
        title: newClientForm.title.trim(),
        company: newClientForm.company.trim(),
        streetAddress: newClientForm.streetAddress.trim(),
        city: newClientForm.city.trim(),
        state: newClientForm.state.trim(),
        zip: newClientForm.zip.trim(),
        phone: newClientForm.phone.trim(),
        email: newClientForm.email.trim(),
      });
      const refreshed = await onLegalEntitiesRefresh();
      const selectedId =
        refreshed.find((e) => e.id === created.id)?.id ?? created.id;
      setLegalEntityId(selectedId);
      setIsAddClientOpen(false);
      resetNewClientForm();
    } catch (err: unknown) {
      setAddClientError(
        err instanceof Error ? err.message : "Failed to add client",
      );
    } finally {
      setIsCreatingClient(false);
    }
  }, [
    isCreatingClient,
    onCreateLegalEntity,
    onLegalEntitiesRefresh,
    newClientForm,
    resetNewClientForm,
  ]);

  const handleCreateRepresentative = useCallback(async () => {
    if (isCreatingRepresentative) return;
    setAddRepresentativeSubmitAttempted(true);
    const validationErrors = validateAddRepresentativeInput(newRepresentativeForm);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      setIsCreatingRepresentative(true);
      setAddRepresentativeError(null);
      const created = await onCreateRepresentative({
        name: newRepresentativeForm.name.trim(),
        title: newRepresentativeForm.title.trim(),
        company: newRepresentativeForm.company.trim(),
        streetAddress: newRepresentativeForm.streetAddress.trim(),
        city: newRepresentativeForm.city.trim(),
        state: newRepresentativeForm.state.trim(),
        zip: newRepresentativeForm.zip.trim(),
        phone: newRepresentativeForm.phone.trim(),
        email: newRepresentativeForm.email.trim(),
      });
      const refreshed = await onRepresentativesRefresh();
      const selectedId =
        refreshed.find((r) => r.id === created.id)?.id ?? created.id;
      setRepresentativeId(selectedId);
      setIsAddRepresentativeOpen(false);
      resetNewRepresentativeForm();
    } catch (err: unknown) {
      setAddRepresentativeError(
        err instanceof Error ? err.message : "Failed to add representative",
      );
    } finally {
      setIsCreatingRepresentative(false);
    }
  }, [
    isCreatingRepresentative,
    onCreateRepresentative,
    onRepresentativesRefresh,
    newRepresentativeForm,
    resetNewRepresentativeForm,
  ]);

  const handleToggleBusinessUnit = useCallback((code: string) => {
    setSelectedBusinessUnits((prev) =>
      prev.includes(code)
        ? prev.filter((item) => item !== code)
        : [...prev, code],
    );
  }, []);

  const handleSelectAllBusinessUnits = useCallback(() => {
    setSelectedBusinessUnits(selectableBusinessUnits.map((o) => o.code));
  }, [selectableBusinessUnits]);

  const updateNewClientField = useCallback(
    <K extends keyof CreateLegalEntityInput>(
      field: K,
      value: CreateLegalEntityInput[K],
    ) => {
      setNewClientForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const updateNewRepresentativeField = useCallback(
    <K extends keyof CreateRepresentativeInput>(
      field: K,
      value: CreateRepresentativeInput[K],
    ) => {
      setNewRepresentativeForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-foreground">
          Contract Review Details
        </h2>
        <p
          className="text-sm text-muted-foreground truncate"
          title={template.name}
        >
          {template.name}
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label
            htmlFor="contract-name"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Contract Name <span className="text-destructive">*</span>
          </label>
          <input
            id="contract-name"
            type="text"
            value={contractName}
            onChange={(e) => {
              if (!isContractNameLocked) setContractName(e.target.value);
            }}
            readOnly={isContractNameLocked}
            disabled={isContractNameLocked}
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-90"
            title={isContractNameLocked ? contractName : undefined}
          />
        </div>

        <div>
          <label
            htmlFor="contract-type"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Contract Type
          </label>
          <input
            id="contract-type"
            type="text"
            value={template.templateType}
            disabled
            readOnly
            className="w-full rounded-lg border border-[var(--border)] bg-muted px-3 py-2 text-sm text-muted-foreground"
          />
        </div>

        <div>
          <label
            htmlFor="business-unit"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Business Unit <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <button
              id="business-unit"
              type="button"
              onClick={() => setIsBusinessUnitOpen((prev) => !prev)}
              className="flex w-full items-center justify-between rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
            >
              <span className="truncate">{selectedBusinessUnitDisplay}</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
            {isBusinessUnitOpen && (
              <div className="absolute z-20 mt-1 w-full rounded-lg border border-[var(--border)] bg-white py-1 shadow-lg">
                <button
                  type="button"
                  className="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm font-medium text-primary"
                  onClick={handleSelectAllBusinessUnits}
                >
                  Select all
                </button>
                {selectableBusinessUnits.map((row) => {
                  const checked = selectedBusinessUnits.includes(row.code);
                  return (
                    <label
                      key={row.code}
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggleBusinessUnit(row.code)}
                      />
                      <span>{row.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          {allBusinessUnitsSelected && (
            <p className="mt-1 text-xs text-muted-foreground">
              All business units selected.
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="contract-origination"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Contract Origination
          </label>
          <select
            id="contract-origination"
            value={contractOrigination}
            onChange={(e) => setContractOrigination(e.target.value)}
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
          >
            <option value="">Select origination</option>
            {CONTRACT_ORIGINATIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="effective-date"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Effective Date <span className="text-destructive">*</span>
          </label>
          <input
            id="effective-date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label
            htmlFor="plan-group-name"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Plan/Group Name{" "}
            <span className="text-muted-foreground">(optional)</span>
          </label>
          <input
            id="plan-group-name"
            type="text"
            value={planGroupName}
            onChange={(e) => setPlanGroupName(e.target.value)}
            placeholder="Enter plan or group name"
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <label
              htmlFor="client-name"
              className="block text-sm font-medium text-foreground"
            >
              Client Name{" "}
              <span className="text-muted-foreground">(optional)</span>
            </label>
            <button
              type="button"
              onClick={() => {
                setIsAddClientOpen(true);
                setAddClientError(null);
              }}
              className="text-xs font-medium text-primary hover:underline"
            >
              + Add Client
            </button>
          </div>
          <select
            id="client-name"
            value={legalEntityId}
            onChange={(e) => setLegalEntityId(e.target.value)}
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
          >
            <option value="">Select client (optional)</option>
            {legalEntities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          {legalEntitiesError && (
            <p className="mt-1 text-xs text-muted-foreground">
              {legalEntitiesError}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="contact-name-ro"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Name
          </label>
          <input
            id="contact-name-ro"
            type="text"
            value={contactNameDisplay}
            readOnly
            placeholder="Select client (optional)"
            className="w-full rounded-lg border border-[var(--border)] bg-muted px-3 py-2 text-sm text-muted-foreground"
          />
        </div>

        <div>
          <label
            htmlFor="contact-title-ro"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Title
          </label>
          <input
            id="contact-title-ro"
            type="text"
            value={contactTitleDisplay}
            readOnly
            placeholder="Select client (optional)"
            className="w-full rounded-lg border border-[var(--border)] bg-muted px-3 py-2 text-sm text-muted-foreground"
          />
        </div>

        <div>
          <label
            htmlFor="address-ro"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Address
          </label>
          <textarea
            id="address-ro"
            value={addressDisplay}
            readOnly
            placeholder="Select client (optional)"
            rows={3}
            className="w-full resize-none rounded-lg border border-[var(--border)] bg-muted px-3 py-2 text-sm text-muted-foreground"
          />
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <label
              htmlFor="representative-name"
              className="block text-sm font-medium text-foreground"
            >
              Representative Name{" "}
              <span className="text-muted-foreground">(optional)</span>
            </label>
            <button
              type="button"
              onClick={() => {
                setIsAddRepresentativeOpen(true);
                setAddRepresentativeError(null);
              }}
              className="text-xs font-medium text-primary hover:underline"
            >
              + Add Representative
            </button>
          </div>
          <select
            id="representative-name"
            value={representativeId}
            onChange={(e) => setRepresentativeId(e.target.value)}
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
          >
            <option value="">Select representative (optional)</option>
            {representatives.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          {representativesError && (
            <p className="mt-1 text-xs text-muted-foreground">
              {representativesError}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="representative-contact-name-ro"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Name
          </label>
          <input
            id="representative-contact-name-ro"
            type="text"
            value={representativeNameDisplay}
            readOnly
            placeholder="Select representative (optional)"
            className="w-full rounded-lg border border-[var(--border)] bg-muted px-3 py-2 text-sm text-muted-foreground"
          />
        </div>

        <div>
          <label
            htmlFor="representative-title-ro"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Title
          </label>
          <input
            id="representative-title-ro"
            type="text"
            value={representativeTitleDisplay}
            readOnly
            placeholder="Select representative (optional)"
            className="w-full rounded-lg border border-[var(--border)] bg-muted px-3 py-2 text-sm text-muted-foreground"
          />
        </div>

        <div>
          <label
            htmlFor="representative-address-ro"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Address
          </label>
          <textarea
            id="representative-address-ro"
            value={representativeAddressDisplay}
            readOnly
            placeholder="Select representative (optional)"
            rows={3}
            className="w-full resize-none rounded-lg border border-[var(--border)] bg-muted px-3 py-2 text-sm text-muted-foreground"
          />
        </div>
      </div>

      {isAddClientOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg border border-[var(--border)] bg-white p-4 shadow-lg">
            <h3 className="text-base font-semibold text-foreground">
              Add Client
            </h3>
            <p className="mb-3 text-xs text-muted-foreground">
              Create a legal entity and select it.
            </p>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Company"
                value={newClientForm.company}
                title={visibleAddClientErrors.company}
                onChange={(e) =>
                  updateNewClientField("company", e.target.value)
                }
                onBlur={() =>
                  setAddClientTouched((p) => ({ ...p, company: true }))
                }
                className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
              />
              {visibleAddClientErrors.company ? (
                <p className="text-xs text-destructive">
                  {visibleAddClientErrors.company}
                </p>
              ) : null}
              <input
                type="text"
                placeholder="Street Address"
                value={newClientForm.streetAddress}
                title={visibleAddClientErrors.streetAddress}
                onChange={(e) =>
                  updateNewClientField("streetAddress", e.target.value)
                }
                onBlur={() =>
                  setAddClientTouched((p) => ({ ...p, streetAddress: true }))
                }
                className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
              />
              {visibleAddClientErrors.streetAddress ? (
                <p className="text-xs text-destructive">
                  {visibleAddClientErrors.streetAddress}
                </p>
              ) : null}
              <div className="grid grid-cols-3 gap-2">
                <InputWithDropdown
                  aria-label="State"
                  title={visibleAddClientErrors.state}
                  placeholder="State"
                  options={locationStates}
                  value={newClientForm.state}
                  onChange={(state) => {
                    const resets = resolveLocationFieldResets(
                      newClientForm.state,
                      state,
                      newClientForm.city,
                      newClientForm.city,
                    );
                    setNewClientForm((prev) => ({
                      ...prev,
                      state,
                      city: resets.resetCity ? "" : prev.city,
                      zip: resets.resetZip ? "" : prev.zip,
                    }));
                  }}
                  onBlur={() =>
                    setAddClientTouched((p) => ({ ...p, state: true }))
                  }
                />
                <InputWithDropdown
                  aria-label="City"
                  title={visibleAddClientErrors.city}
                  placeholder="City"
                  options={locationCities}
                  value={newClientForm.city}
                  onChange={(city) => {
                    const resets = resolveLocationFieldResets(
                      newClientForm.state,
                      newClientForm.state,
                      newClientForm.city,
                      city,
                    );
                    setNewClientForm((prev) => ({
                      ...prev,
                      city,
                      zip: resets.resetZip ? "" : prev.zip,
                    }));
                  }}
                  onBlur={() =>
                    setAddClientTouched((p) => ({ ...p, city: true }))
                  }
                  disabled={!newClientForm.state.trim()}
                />
                <InputWithDropdown
                  aria-label="Zip"
                  title={visibleAddClientErrors.zip}
                  type="tel"
                  inputMode="numeric"
                  maxLength={5}
                  pattern="[0-9]*"
                  placeholder="Zip"
                  options={locationZips}
                  value={newClientForm.zip}
                  onChange={(zip) =>
                    updateNewClientField(
                      "zip",
                      zip.replace(/\D/g, "").slice(0, 5),
                    )
                  }
                  onBlur={() =>
                    setAddClientTouched((p) => ({ ...p, zip: true }))
                  }
                  disabled={!newClientForm.city.trim()}
                />
              </div>
              {(visibleAddClientErrors.city ||
                visibleAddClientErrors.state ||
                visibleAddClientErrors.zip) && (
                <p className="text-xs text-destructive">
                  {visibleAddClientErrors.city ??
                    visibleAddClientErrors.state ??
                    visibleAddClientErrors.zip}
                </p>
              )}
              {locationStates.length === 0 ? (
                <p className="text-xs text-muted-foreground" role="status">
                  Location suggestions are unavailable. You can still type city,
                  state, and zip manually.
                </p>
              ) : null}
            </div>
            {addClientError && (
              <p className="mt-2 text-xs text-destructive" role="alert">
                {addClientError}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsAddClientOpen(false);
                  resetNewClientForm();
                }}
                className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleCreateClient();
                }}
                disabled={
                  isCreatingClient ||
                  (addClientSubmitAttempted && hasNewClientValidationErrors)
                }
                className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
              >
                {isCreatingClient ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddRepresentativeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg border border-[var(--border)] bg-white p-4 shadow-lg">
            <h3 className="text-base font-semibold text-foreground">
              Add Representative
            </h3>
            <p className="mb-3 text-xs text-muted-foreground">
              Create a representative and select it.
            </p>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Name"
                value={newRepresentativeForm.name}
                title={visibleAddRepresentativeErrors.name}
                onChange={(e) =>
                  updateNewRepresentativeField("name", e.target.value)
                }
                onBlur={() =>
                  setAddRepresentativeTouched((p) => ({ ...p, name: true }))
                }
                className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
              />
              {visibleAddRepresentativeErrors.name ? (
                <p className="text-xs text-destructive">
                  {visibleAddRepresentativeErrors.name}
                </p>
              ) : null}
              <input
                type="text"
                placeholder="Title"
                value={newRepresentativeForm.title}
                title={visibleAddRepresentativeErrors.title}
                onChange={(e) =>
                  updateNewRepresentativeField("title", e.target.value)
                }
                onBlur={() =>
                  setAddRepresentativeTouched((p) => ({ ...p, title: true }))
                }
                className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
              />
              {visibleAddRepresentativeErrors.title ? (
                <p className="text-xs text-destructive">
                  {visibleAddRepresentativeErrors.title}
                </p>
              ) : null}
              <input
                type="text"
                placeholder="Company"
                value={newRepresentativeForm.company}
                title={visibleAddRepresentativeErrors.company}
                onChange={(e) =>
                  updateNewRepresentativeField("company", e.target.value)
                }
                onBlur={() =>
                  setAddRepresentativeTouched((p) => ({ ...p, company: true }))
                }
                className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
              />
              {visibleAddRepresentativeErrors.company ? (
                <p className="text-xs text-destructive">
                  {visibleAddRepresentativeErrors.company}
                </p>
              ) : null}
              <input
                type="text"
                placeholder="Street Address"
                value={newRepresentativeForm.streetAddress}
                title={visibleAddRepresentativeErrors.streetAddress}
                onChange={(e) =>
                  updateNewRepresentativeField("streetAddress", e.target.value)
                }
                onBlur={() =>
                  setAddRepresentativeTouched((p) => ({
                    ...p,
                    streetAddress: true,
                  }))
                }
                className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
              />
              {visibleAddRepresentativeErrors.streetAddress ? (
                <p className="text-xs text-destructive">
                  {visibleAddRepresentativeErrors.streetAddress}
                </p>
              ) : null}
              <div className="grid grid-cols-3 gap-2">
                <InputWithDropdown
                  aria-label="Representative State"
                  title={visibleAddRepresentativeErrors.state}
                  placeholder="State"
                  options={representativeLocationStates}
                  value={newRepresentativeForm.state}
                  onChange={(state) => {
                    const resets = resolveLocationFieldResets(
                      newRepresentativeForm.state,
                      state,
                      newRepresentativeForm.city,
                      newRepresentativeForm.city,
                    );
                    setNewRepresentativeForm((prev) => ({
                      ...prev,
                      state,
                      city: resets.resetCity ? "" : prev.city,
                      zip: resets.resetZip ? "" : prev.zip,
                    }));
                  }}
                  onBlur={() =>
                    setAddRepresentativeTouched((p) => ({ ...p, state: true }))
                  }
                />
                <InputWithDropdown
                  aria-label="Representative City"
                  title={visibleAddRepresentativeErrors.city}
                  placeholder="City"
                  options={representativeLocationCities}
                  value={newRepresentativeForm.city}
                  onChange={(city) => {
                    const resets = resolveLocationFieldResets(
                      newRepresentativeForm.state,
                      newRepresentativeForm.state,
                      newRepresentativeForm.city,
                      city,
                    );
                    setNewRepresentativeForm((prev) => ({
                      ...prev,
                      city,
                      zip: resets.resetZip ? "" : prev.zip,
                    }));
                  }}
                  onBlur={() =>
                    setAddRepresentativeTouched((p) => ({ ...p, city: true }))
                  }
                  disabled={!newRepresentativeForm.state.trim()}
                />
                <InputWithDropdown
                  aria-label="Representative Zip"
                  title={visibleAddRepresentativeErrors.zip}
                  type="tel"
                  inputMode="numeric"
                  maxLength={5}
                  pattern="[0-9]*"
                  placeholder="Zip"
                  options={representativeLocationZips}
                  value={newRepresentativeForm.zip}
                  onChange={(zip) =>
                    updateNewRepresentativeField(
                      "zip",
                      zip.replace(/\D/g, "").slice(0, 5),
                    )
                  }
                  onBlur={() =>
                    setAddRepresentativeTouched((p) => ({ ...p, zip: true }))
                  }
                  disabled={!newRepresentativeForm.city.trim()}
                />
              </div>
              {(visibleAddRepresentativeErrors.city ||
                visibleAddRepresentativeErrors.state ||
                visibleAddRepresentativeErrors.zip) && (
                <p className="text-xs text-destructive">
                  {visibleAddRepresentativeErrors.city ??
                    visibleAddRepresentativeErrors.state ??
                    visibleAddRepresentativeErrors.zip}
                </p>
              )}
              {representativeLocationStates.length === 0 ? (
                <p className="text-xs text-muted-foreground" role="status">
                  Location suggestions are unavailable. You can still type city,
                  state, and zip manually.
                </p>
              ) : null}
              <PhoneNumberInput
                placeholder="Phone"
                value={newRepresentativeForm.phone}
                title={visibleAddRepresentativeErrors.phone}
                onChange={(phone) =>
                  setNewRepresentativeForm((prev) => ({ ...prev, phone }))
                }
                onBlur={() => setAddRepresentativePhoneTouched(true)}
              />
              {visibleAddRepresentativeErrors.phone ? (
                <p className="text-xs text-destructive">
                  {visibleAddRepresentativeErrors.phone}
                </p>
              ) : null}
              <input
                type="email"
                placeholder="Email"
                value={newRepresentativeForm.email}
                title={visibleAddRepresentativeErrors.email}
                onChange={(e) =>
                  setNewRepresentativeForm((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                onBlur={() => setAddRepresentativeEmailTouched(true)}
                className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
              />
              {visibleAddRepresentativeErrors.email ? (
                <p className="text-xs text-destructive">
                  {visibleAddRepresentativeErrors.email}
                </p>
              ) : null}
            </div>
            {addRepresentativeError && (
              <p className="mt-2 text-xs text-destructive" role="alert">
                {addRepresentativeError}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsAddRepresentativeOpen(false);
                  resetNewRepresentativeForm();
                }}
                className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleCreateRepresentative();
                }}
                disabled={
                  isCreatingRepresentative ||
                  (addRepresentativeSubmitAttempted &&
                    hasNewRepresentativeValidationErrors)
                }
                className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
              >
                {isCreatingRepresentative ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* <div className="space-y-2"> */}
      <div className="flex flex-col gap-2">
        {hostPersistSuccess || graphPersistSuccess ? (
          <p
            className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-foreground"
            role="status"
          >
            {hostPersistSuccess ?? graphPersistSuccess}
          </p>
        ) : null}
        {hostPersistError || graphPersistError ? (
          <p
            className="rounded-lg border border-destructive/30 px-3 py-2 text-sm text-destructive"
            role="alert"
          >
            {hostPersistError ?? graphPersistError}
          </p>
        ) : null}
        <button
          type="button"
          onClick={handleApplyChanges}
          disabled={!isCoreFormValid || isApplyingChanges}
          className="flex-1 w-full rounded-lg border border-[var(--border)] bg-input-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isApplyingChanges
            ? "Applying…"
            : hasAppliedChanges
              ? "Reapply and Review Changes"
              : "Apply and Review Changes"}
        </button>
        {hasChangesAfterApply ? (
          <p className="text-xs text-muted-foreground" role="status">
            Form values changed after the last apply. Reapply changes before
            generating.
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            void handleGenerateContract();
          }}
          disabled={!canGenerate}
          className="flex-1 w-full rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGeneratingContract ? "Generating…" : "Generate Contract"}
        </button>
      </div>
      {/* </div> */}
      {applyError && (
        <p className="text-xs text-destructive" role="alert">
          {applyError}
        </p>
      )}
      {requiresManualRevert ? (
        <button
          type="button"
          onClick={handleManualRevertConfirmed}
          className="w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
        >
          I reverted changes
        </button>
      ) : null}
      {/* </div> */}
    </div>
  );
};
